import { PlatformSiteAri } from '@atlassian/ari';

import { getIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/getIsNav4Enabled.ts';
import { getIsActivityV3RecentsEnabled } from '@confluence/activity-v3-enabled/entry-points/getIsActivityV3RecentsEnabled.ts';

import { ActivitiesObjectType, ActivityEventType } from '../homeViewsQueryHelpers.ts';

import { HomeRecentActivitiesQuery, HomeRecentCreatedQuery } from './HomeRecentQueries.graphql.ts';
import { HomeRecentActivityQuery } from './HomeRecentQueriesV3.graphql.ts';

// This list matches the event types Activity Platform uses for
// activities.myActivities.workedOn with CREATED and UPDATED
// removed as those events are only used in Jira
// https://bitbucket.org/atlassian/worklens/src/bff1a264119c0121e1e7ec7787442cdf0e177f40/src/main/java/com/atlassian/worklens/settings/EventsSettings.java#lines-38
const WORKED_ON_EVENT_TYPES = [
	ActivityEventType.COMMENTED,
	ActivityEventType.EDITED,
	ActivityEventType.PUBLISHED,
	ActivityEventType.UPDATED,
];

const WORKED_ON_EVENT_TYPES_V3 = [
	ActivityEventType.EDITED,
	ActivityEventType.PUBLISHED,
	ActivityEventType.UPDATED,
	ActivityEventType.CREATED,
	ActivityEventType.STARTED,
	ActivityEventType.SNAPSHOTTED,
];

const COLLABORATED_ON_EVENT_TYPES = [ActivityEventType.COMMENTED, ...WORKED_ON_EVENT_TYPES_V3];

const OBJECT_TYPES = [
	ActivitiesObjectType.PAGE,
	ActivitiesObjectType.BLOGPOST,
	ActivitiesObjectType.EMBED,
];

const PAGE_SIZE = 24;

export const getRecentQueryOptions = ({
	pill,
	cloudId,
	endCursor,
	isWhiteboardViewedEnabled,
	isWhiteboardUpdatedEnabled,
	isDatabaseEnabled,
}: {
	pill: string | undefined;
	cloudId: string;
	endCursor: string;
	isWhiteboardViewedEnabled?: boolean;
	isWhiteboardUpdatedEnabled?: boolean;
	isDatabaseEnabled?: boolean;
}) => {
	const isNav4Enabled = getIsNav4Enabled();
	const createdDateFilter = isNav4Enabled ? 'and created >= startOfDay("-90d") ' : '';
	const isActivityV3RecentsEnabled = getIsActivityV3RecentsEnabled();

	const activityQuery = isActivityV3RecentsEnabled
		? HomeRecentActivityQuery
		: HomeRecentActivitiesQuery;
	const activityCloudId = isActivityV3RecentsEnabled
		? PlatformSiteAri.create({ siteId: cloudId }).toString()
		: cloudId;

	switch (pill) {
		case 'created':
			return {
				query: HomeRecentCreatedQuery,
				variables: {
					cql: `type in (page, blogpost, embed${
						isWhiteboardUpdatedEnabled ? ', whiteboard' : ''
					}${isDatabaseEnabled ? ', database' : ''}) and owner=currentUser() ${createdDateFilter}order by created desc`,
					cqlcontext: `{"contentStatuses":["draft", "current"]}`,
					first: PAGE_SIZE,
					after: endCursor,
				},
			};
		case 'worked-on':
			return {
				query: activityQuery,
				variables: {
					cloudId: activityCloudId,
					first: PAGE_SIZE,
					after: endCursor,
					eventTypes: isActivityV3RecentsEnabled ? WORKED_ON_EVENT_TYPES_V3 : WORKED_ON_EVENT_TYPES,
					objectTypes: [
						...OBJECT_TYPES,
						...(isWhiteboardUpdatedEnabled ? [ActivitiesObjectType.WHITEBOARD] : []),
						...(isDatabaseEnabled ? [ActivitiesObjectType.DATABASE] : []),
						...(isActivityV3RecentsEnabled ? [ActivitiesObjectType.CONTENT] : []),
					],
				},
			};
		case 'collaborated-on':
			return {
				query: activityQuery,
				variables: {
					cloudId: PlatformSiteAri.create({ siteId: cloudId }).toString(),
					first: PAGE_SIZE,
					after: endCursor,
					eventTypes: COLLABORATED_ON_EVENT_TYPES,
					objectTypes: [
						...OBJECT_TYPES,
						...(isWhiteboardUpdatedEnabled ? [ActivitiesObjectType.WHITEBOARD] : []),
						...(isDatabaseEnabled ? [ActivitiesObjectType.DATABASE] : []),
						ActivitiesObjectType.CONTENT,
						ActivitiesObjectType.COMMENT,
					],
				},
			};
		case 'all':
		default:
			return {
				query: activityQuery,
				variables: {
					cloudId: activityCloudId,
					first: PAGE_SIZE,
					after: endCursor,
					eventTypes: [],
					objectTypes: [
						...OBJECT_TYPES,
						...(isWhiteboardViewedEnabled ? [ActivitiesObjectType.WHITEBOARD] : []),
						...(isDatabaseEnabled ? [ActivitiesObjectType.DATABASE] : []),
						...(isActivityV3RecentsEnabled ? [ActivitiesObjectType.CONTENT] : []),
					],
				},
			};
	}
};
